import { Loading, Modal } from '@carbon/react';
import { Add, Bullhorn, Checkmark } from '@carbon/icons-react';
import {
    IconEdit,
    IconTrash,
} from '@tabler/icons-react';
import {
    ActionIcon,
    Group,
    Paper,
    Text,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import DOMPurify from 'dompurify';

import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import useGetUserPermission from 'hooks/useGetUserPermission';
import { useMainContainer } from 'Components/Common/Container/context/ContainerContext';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import EmptyState from 'Components/Common/EmptyState/EmptyState';
import InfoCard, { InfoCardItem } from 'Components/Common/InfoCard/InfoCard';
import TableSearchInput from 'Components/Common/TableSearchInput/TableSearchInput';
import sharedMessages from 'Components/utils/sharedMessages';
import {
    AnnouncementDto,
    useDeleteAnnouncementMutation,
    useGetAnnouncementsQuery,
} from 'api/announcementEndpoints';
import AddAnnouncementView from '../AddAnnouncementView/AddAnnouncementView';
import EditAnnouncementView from '../EditAnnouncementView/EditAnnouncementView';

import styles from './AnnouncmentsView.module.scss';
import useGetUser from 'hooks/useGetUser';
import NotConfiguredEmptyState from 'Components/Common/NotConfiguredEmptyState/NotConfiguredEmptyState';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';

const getPlainText = (html: string) =>
    DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim();

const AnnouncementsView = () => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const { isAdmin, isModerator, isOwner } = useGetUserPermission();
    const hasPermission = isAdmin || isModerator || isOwner;
    const {
        activeSubscription,
        isFetching: isFetchingSubscription,
        isPaidPlan,
    } = useGetCurrentSubscription();
    const hasSubscriptionAccess = !isFetchingSubscription && isPaidPlan && activeSubscription?.hasAccess !== false;
    const isTopBarActionDisabled = !hasPermission || !hasSubscriptionAccess;
    const {
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
        setDisabledAction,
        setDisabledActionTooltip,
    } = useMainContainer();

    const [search, setSearch] = useState('');
    const [createAnnouncementOpen, setCreateAnnouncementOpen] = useState(false);
    const [announcementToEdit, setAnnouncementToEdit] = useState<AnnouncementDto | null>(null);
    const [announcementToDelete, setAnnouncementToDelete] = useState<AnnouncementDto | null>(null);

    const { data, isFetching, isError, refetch } = useGetAnnouncementsQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId },
    );

    const [deleteAnnouncement, { isLoading: isDeleting }] = useDeleteAnnouncementMutation();

    useEffect(() => {
        setTopBarAction(() => () => setCreateAnnouncementOpen(true));
        setTopBarActionTitle(intl.formatMessage(messages.add));
        setTopBarIcon(<Add size={20} />);
        setDisabledAction(isTopBarActionDisabled);
        setDisabledActionTooltip(intl.formatMessage(messages.addDisabledTooltip));

        return () => {
            setTopBarAction(null);
            setTopBarActionTitle(undefined);
            setTopBarIcon(undefined);
            setDisabledAction(false);
            setDisabledActionTooltip(undefined);
        };
    }, [
        intl,
        isTopBarActionDisabled,
        setDisabledAction,
        setDisabledActionTooltip,
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
    ]);

    const announcements = useMemo(() => data ?? [], [data]);
    const infoCardItems = useMemo<InfoCardItem[]>(() => [
        {
            icon: <Checkmark />,
            title: intl.formatMessage(messages.mobileVisibilityTitle),
            description: intl.formatMessage(messages.mobileVisibilityDescription),
        },
        {
            icon: <Checkmark />,
            title: intl.formatMessage(messages.communicationTitle),
            description: intl.formatMessage(messages.communicationDescription),
        },
        {
            icon: <Checkmark />,
            title: intl.formatMessage(messages.historyTitle),
            description: intl.formatMessage(messages.historyDescription),
        },
    ], [intl]);

    const filteredAnnouncements = useMemo(() => {
        const term = search.toLowerCase().trim();
        if (!term) return announcements;

        return announcements.filter((announcement) =>
            announcement.title.toLowerCase().includes(term) ||
            getPlainText(announcement.content).toLowerCase().includes(term));
    }, [announcements, search]);
    const showEmptyState = filteredAnnouncements.length === 0 && !isFetching;

    const formatDateBadge = (iso: string) => {
        const date = new Date(iso);
        const month = date.toLocaleDateString('pl-PL', { month: 'long' }).toUpperCase();

        return {
            day: date.toLocaleDateString('pl-PL', { day: 'numeric' }),
            month,
            year: date.toLocaleDateString('pl-PL', { year: 'numeric' }),
        };
    };

    const handleDeleteConfirm = async () => {
        if (!announcementToDelete || !currentEntityId) return;

        await deleteAnnouncement({
            entityId: currentEntityId,
            id: announcementToDelete.id,
        }).unwrap();
        setAnnouncementToDelete(null);
    };

    if (isError) {
        return <ErrorActionState className={styles.error} onClick={refetch} />;
    }

    return (
        <>
            {(isFetching || isDeleting) && <Loading />}
            <div className={`${styles.container} ${showEmptyState ? styles.emptyContainer : ''}`}>
                <InfoCard
                    id="announcements-info"
                    className={styles.infoCard}
                    isHorizontal
                    isClosable
                    illustration={<Bullhorn />}
                    title={intl.formatMessage(messages.infoCardTitle)}
                    description={intl.formatMessage(messages.infoCardDescription)}
                    items={infoCardItems}
                />
                {(announcements.length > 0 || search) && (
                    <TableSearchInput
                        className={styles.search}
                        id="search-announcements"
                        placeholder={intl.formatMessage(messages.searchPlaceholder)}
                        value={search}
                        onChange={setSearch}
                    />
                )}
                {showEmptyState ? (
                    <EmptyState title={intl.formatMessage(messages.empty)} />
                ) : (
                    <Paper className={styles.list} radius="md" withBorder>
                        {filteredAnnouncements.map((announcement) => {
                            const dateBadge = formatDateBadge(announcement.publishedAt);
                            const plainContent = getPlainText(announcement.content);

                            return (
                                <div key={announcement.id} className={styles.row}>
                                    <div className={styles.dateCell}>
                                        <span className={styles.day}>{dateBadge.day}</span>
                                        <span className={styles.month}>{dateBadge.month}</span>
                                        <span className={styles.year}>{dateBadge.year}</span>
                                    </div>
                                    <div className={styles.details}>
                                        <Group gap="xs" wrap="nowrap" className={styles.titleRow}>
                                            <Text className={styles.title}>{announcement.title}</Text>
                                        </Group>
                                        <Text className={styles.description}>
                                            {plainContent}
                                        </Text>
                                    </div>
                                    {hasPermission && (
                                        <Group gap="xs" className={styles.actions} wrap="nowrap">
                                            <ActionIcon
                                                className={styles.actionButton}
                                                variant="subtle"
                                                color="gray"
                                                size="xl"
                                                aria-label={intl.formatMessage(messages.edit)}
                                                onClick={() => setAnnouncementToEdit(announcement)}
                                            >
                                                <IconEdit size={22} />
                                            </ActionIcon>
                                            <ActionIcon
                                                className={styles.actionButton}
                                                variant="subtle"
                                                color="red"
                                                size="xl"
                                                aria-label={intl.formatMessage(messages.delete)}
                                                onClick={() => setAnnouncementToDelete(announcement)}
                                            >
                                                <IconTrash size={22} />
                                            </ActionIcon>
                                        </Group>
                                    )}
                                </div>
                            );
                        })}
                    </Paper>
                )}
            </div>
            <AddAnnouncementView
                open={createAnnouncementOpen}
                onClose={() => setCreateAnnouncementOpen(false)}
            />
            <EditAnnouncementView
                announcement={announcementToEdit}
                open={!!announcementToEdit}
                onClose={() => setAnnouncementToEdit(null)}
            />
            <Modal
                open={!!announcementToDelete}
                onRequestClose={() => setAnnouncementToDelete(null)}
                onRequestSubmit={handleDeleteConfirm}
                danger
                modalHeading={intl.formatMessage(messages.deleteConfirm)}
                modalLabel={intl.formatMessage(sharedMessages.irreversibleAction)}
                primaryButtonText={intl.formatMessage(sharedMessages.remove)}
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                secondaryButtonText={intl.formatMessage(sharedMessages.back)}
            />
        </>
    );
};

const AnnouncementsViewStateHandler = () => {
    const currentEntityId = useGetCurrentEntityId();
    const intl = useIntl();

    const user = useGetUser();
            
    if (user && !user.hasInitialSetupCompleted) {
        return (
            <NotConfiguredEmptyState />
        );
    }

    if (!currentEntityId) {
        return <EmptyState title={intl.formatMessage(sharedMessages.noEntitySelected)} />;
    }

    return <AnnouncementsView />;
};

const messages = defineMessages({
    add: {
        id: 'announcements.add',
        defaultMessage: 'Dodaj ogłoszenie',
    },
    addDisabledTooltip: {
        id: 'announcements.addDisabledTooltip',
        defaultMessage: 'Nie masz odpowiednich uprawnień w tej grupie albo nie masz wykupionej odpowiedniej subskrypcji koniecznej do dodania ogłoszenia.',
    },
    empty: {
        id: 'announcements.empty',
        defaultMessage: 'Brak ogłoszeń.',
    },
    searchPlaceholder: {
        id: 'announcements.search',
        defaultMessage: 'Szukaj po tytule lub treści',
    },
    edit: {
        id: 'announcements.edit',
        defaultMessage: 'Edytuj ogłoszenie',
    },
    delete: {
        id: 'announcements.delete',
        defaultMessage: 'Usuń ogłoszenie',
    },
    deleteConfirm: {
        id: 'announcements.deleteConfirm',
        defaultMessage: 'Czy na pewno chcesz usunąć to ogłoszenie?',
    },
    infoCardTitle: {
        id: 'announcements.infoCard.title',
        defaultMessage: 'Jak to działa?',
    },
    infoCardDescription: {
        id: 'announcements.infoCard.description',
        defaultMessage: 'Ogłoszenia są widoczne dla parafian w aplikacji mobilnej. To dobre miejsce na krótkie komunikaty, informacje organizacyjne i przypomnienia, które powinny szybko dotrzeć do wspólnoty.',
    },
    mobileVisibilityTitle: {
        id: 'announcements.infoCard.mobileVisibilityTitle',
        defaultMessage: 'Widoczne w aplikacji',
    },
    mobileVisibilityDescription: {
        id: 'announcements.infoCard.mobileVisibilityDescription',
        defaultMessage: 'Parafianie zobaczą ogłoszenia na ekranie „Ogłoszenia” w aplikacji.',
    },
    communicationTitle: {
        id: 'announcements.infoCard.communicationTitle',
        defaultMessage: 'Szybsza komunikacja',
    },
    communicationDescription: {
        id: 'announcements.infoCard.communicationDescription',
        defaultMessage: 'Jedno ogłoszenie pomaga przekazać ważną informację bez powtarzania jej w wielu miejscach.',
    },
    historyTitle: {
        id: 'announcements.infoCard.historyTitle',
        defaultMessage: 'Porządek w informacjach',
    },
    historyDescription: {
        id: 'announcements.infoCard.historyDescription',
        defaultMessage: 'Parafianie mogą wrócić do treści ogłoszenia wtedy, gdy będą jej potrzebować.',
    },
});

export default AnnouncementsViewStateHandler;
